import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { MediaSearchTool } from 'js/src/agent/tools/MediaSearchTool';

export const createMediaSlideshowVideoSkill: Skill = {
    name: SKILL_NAMES.createMediaSlideshowVideo,
    description:
        'Uploaded/attached media showcased — a real estate tour, event recap, product showcase, photo highlight reel. For when the uploads themselves are the point of the video.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Media showcase',
        blurb: 'Show off your own photos and clips in sequence.',
        icon: 'collections',
        gradient: ['#7A6796', '#96679B'],
    },
    get instructions(): string {
        return `# Create Media Slideshow Video

For showcasing media the user has uploaded or attached — real estate tours, event
recaps, product showcases, photo highlight reels. Their media is the star; text is
secondary and must never obscure it.

## Start from the uploads
- Build the video from the media the user attached — those are what they want
  showcased. Load the \`${SKILL_NAMES.findUserMedia}\` skill if you need more from their library
  than what's attached to the chat.
- Stock media (\`${MediaSearchTool.toolName}\`) is a fallback only, for beats the uploads genuinely
  can't cover (a title card, a CTA).

## GOLDEN RULE — never duplicate media across scenes
Each uploaded item appears in exactly ONE scene. Reusing the same media in multiple
scenes causes ugly animation restarts. When there are more script beats than media
items, do NOT repeat media — instead:
- Keep one media on the scene and sequence the text across 2-3 subscenes — good for
  splitting a longer thought over one establishing shot. The \`${SKILL_NAMES.textVideoBasics}\`
  skill has the subscene mechanics.
- Or use text-only scenes for titles, chapter breaks, CTAs, and emphasis.

It's better to leave an upload unused than to force the same media into two scenes.

## Orientation-aware design
- **Landscape media** (wider than tall) → fullbleed / media-focused designs where
  the media fills the frame and text sits minimally at the edges.
- **Portrait media** (taller than wide) → inset / side-by-side designs that show the
  full image without cropping. Do NOT put portrait media in a fullbleed design — it
  crops badly.
- Match the aspect ratio to the uploads: mostly-landscape media → landscape video;
  mostly-portrait → vertical.
- Vary the design from scene to scene — don't repeat the same design back-to-back.
- When reviewing, look specifically for the slideshow failures: portrait media
  squished or cropped, faces or key subjects hidden under text (the \`${SKILL_NAMES.cropMedia}\`
  skill has the reframe procedure).

## Showcase prominently, match semantically
- Keep text minimal so it never covers the key content of the image/video.
- Only place a piece of media where it genuinely fits the beat. Quality over
  quantity — a poor match is worse than no media.

## Narration and music
- The default is no narration: text and media carry the message, so add background
  music unless it clearly doesn't suit the video.
- Narration is fine when the user wants it — add an AI voiceover scene group (load
  the \`${SKILL_NAMES.voiceoverVideoBasics}\` skill) and keep every rule here: the uploads become
  the overlay scenes, still one scene per item.`;
    },
};
